import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { clearCart } from "../lib/cart";
import { useSEO } from "../components/site/Reveal";

const DEST = {
  shop: { to: "/shop", label: "Continue shopping" },
  course: { to: "/courses", label: "Back to Courses" },
  event: { to: "/events", label: "Back to Events" },
  donation: { to: "/support", label: "Back to Support" },
};

export default function PayCallback() {
  useSEO("Payment", "Confirming your Paystack payment with ÀsÀNigHT Worldwide.");
  const [params] = useSearchParams();
  const reference = params.get("reference") || params.get("trxref") || "";
  const [state, setState] = React.useState("verifying"); // verifying | success | failed
  const [purpose, setPurpose] = React.useState("");

  React.useEffect(() => {
    if (!reference) { setState("failed"); return; }
    api.get(`/paystack/verify/${reference}`)
      .then(({ data }) => {
        if (data.status === "success") {
          setPurpose(data.purpose || "");
          if (data.purpose === "shop") { try { clearCart(); } catch (e) { console.warn("Cart could not be cleared:", e); } }
          setState("success");
        } else {
          setState("failed");
        }
      })
      .catch(() => setState("failed"));
  }, [reference]);

  const dest = DEST[purpose] || { to: "/", label: "Back home" };

  return (
    <div className="min-h-screen grid place-items-center px-6 py-28">
      <div className="max-w-md w-full bg-cream border border-parchment rounded-2xl p-10 text-center shadow-xl shadow-ink/5" data-testid="pay-callback-card">
        {state === "verifying" && (
          <div data-testid="pay-verifying">
            <Loader2 className="w-12 h-12 text-forest mx-auto mb-4 animate-spin" />
            <h1 className="font-serif text-2xl text-ink mb-1">Confirming your payment…</h1>
            <p className="text-ink-muted text-sm">Please wait a moment while we verify with Paystack.</p>
          </div>
        )}
        {state === "success" && (
          <div data-testid="pay-success">
            <CheckCircle2 className="w-14 h-14 text-forest mx-auto mb-4" />
            <h1 className="font-serif text-3xl text-ink mb-2">Ẹ ṣé púpọ̀! Payment received.</h1>
            <p className="text-ink-muted mb-6">
              {purpose === "donation"
                ? "Thank you for your generous donation. Your support keeps Yorùbá heritage alive."
                : purpose === "event"
                ? "Your trip registration is confirmed. We'll be in touch with the next steps."
                : purpose === "course"
                ? "You're enrolled. Welcome to the Learning Portal!"
                : "Your order is confirmed. A receipt has been sent to your email."}
            </p>
            <Link to={dest.to} data-testid="pay-success-cta" className="inline-flex bg-forest text-cream font-semibold px-6 py-3 rounded-full hover:bg-forest-light transition-colors">
              {dest.label}
            </Link>
          </div>
        )}
        {state === "failed" && (
          <div data-testid="pay-failed">
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <h1 className="font-serif text-2xl text-ink mb-2">Payment not completed</h1>
            <p className="text-ink-muted mb-6">We couldn't confirm this payment. If money was deducted, it will be refunded automatically. Please try again.</p>
            <Link to={dest.to} data-testid="pay-failed-cta" className="inline-flex bg-gold text-night font-semibold px-6 py-3 rounded-full hover:bg-gold-light transition-colors">
              {dest.label}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
